import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { FiAlertCircle, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../SubscriptionContext';
import { buildAccountVM } from '../utils/subscription';
import './AccountPanel.css';

// The account half of the profile card: which plan, which store, the two dates
// that matter, and the two things a player can actually do about it.
//
// Every figure comes from `buildAccountVM`, which is where the store's live
// answer and the server's webhook-fed copy are reconciled. Nothing is decided
// here — this file lays out what that returns.
//
// THERE IS NO CANCEL BUTTON, and its absence is the design. Neither Apple nor
// Google permits an app to cancel a subscription on the user's behalf; the only
// thing any app can do is send them to the store's own subscriptions page,
// which is where cancelling, changing plan and resubscribing all happen. A
// button labelled "Cancel" that opens a web page is a button that lies about
// what it did — and worse, a player who taps it, sees the store, and backs out
// believes they have cancelled. So the button says where it goes.

const noteFor = (vm) => {
  switch (vm.status) {
    case 'billing_issue':
      return 'Your last payment didn’t go through. Update your payment details '
        + 'in the store to keep your subscription.';
    case 'cancelling':
      return 'Your subscription won’t renew. You keep Pro until the date above.';
    case 'lapsed':
      return 'Your subscription has ended. Resubscribe in the store to get Pro back.';
    case 'complimentary':
      return 'Pro is on your account directly — there’s nothing to pay and '
        + 'nothing to renew.';
    case 'trial':
      return 'You’re on a free trial. It becomes a paid subscription on the date above '
        + 'unless you cancel before then.';
    default:
      return null;
  }
};

const AccountPanel = () => {
  const { userProfile } = useAuth();
  const { customerInfo, restore, refresh, isBypassed } = useSubscription();
  const [busy, setBusy] = useState(false);
  // One line under the buttons, for the one thing a Restore has to be able to
  // say: a restore that finds nothing does NOT throw, so without this the
  // button spins and settles and the player is told nothing at all. (The
  // paywall learned the same lesson — see handleRestore in Paywall.js.)
  const [message, setMessage] = useState(null);

  const isNative = Capacitor.isNativePlatform();
  const vm = buildAccountVM({
    profile: userProfile,
    customerInfo,
    isNative,
    isBypassed,
  });

  const handleRestore = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await restore();
      await refresh();
      const ci = result?.customerInfo ?? result;
      const active = ci?.entitlements?.active || {};
      const subs = ci?.activeSubscriptions || [];
      if (Object.keys(active).length === 0 && subs.length === 0) {
        setMessage({ tone: 'warn', text: 'No previous purchase found on this account.' });
      } else {
        setMessage({ tone: 'good', text: 'Purchases restored.' });
      }
    } catch (e) {
      setMessage({
        tone: 'warn',
        text: 'Couldn’t reach the store. Check your connection and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  // `_blank` is what this app already uses to leave the WebView
  // (WhatsAppShareModal); Capacitor hands it to the system, which is what puts
  // an apps.apple.com subscriptions link in the App Store app rather than in a
  // browser tab inside our own shell.
  const handleManage = () => {
    if (vm.manageUrl) window.open(vm.manageUrl, '_blank');
  };

  const note = noteFor(vm);

  return (
    <div className="acct-panel">
      <div className="acct-head">
        <span className="acct-title">Account</span>
        <span className={`acct-plan acct-plan--${vm.isPro ? 'pro' : 'free'}`}>
          {vm.planLabel}
        </span>
        {/* The plan and the STATE of it are two different claims — "Pro" and
            "cancels at period end" are both true at once — so they are two
            chips. On a plain free account they are the SAME claim, and two
            chips side by side both reading "Free" is a second chip saying
            nothing. */}
        {vm.statusLabel !== vm.planLabel && (
          <span className={`acct-status acct-status--${vm.tone}`}>{vm.statusLabel}</span>
        )}
      </div>

      {vm.rows.length > 0 && (
        <dl className="acct-rows">
          {vm.rows.map((row) => (
            <div className="acct-row" key={row.key}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {note && (
        <p className={`acct-note acct-note--${vm.tone}`}>
          {vm.tone === 'warn' && <FiAlertCircle size={13} aria-hidden="true" />}
          {note}
        </p>
      )}

      {/* Nothing to say about a store on the website beyond where to go. A free
          web user is not being sold to here — there is no purchase path in a
          browser, so an "Upgrade" button would lead nowhere. */}
      {!isNative && vm.status === 'free' && (
        <p className="acct-note">
          Pro is bought in the 1-19 app on iPhone or Android.
        </p>
      )}

      {(vm.canManage || vm.canRestore) && (
        <div className="acct-actions">
          {vm.canManage && (
            <button type="button" className="acct-btn" onClick={handleManage}>
              <FiExternalLink size={14} aria-hidden="true" />
              {/* Named for where it goes, because that is all this button can
                  honestly promise — see the note at the top of this file. */}
              Manage in {vm.storeLabel}
            </button>
          )}
          {vm.canRestore && (
            <button
              type="button"
              className="acct-btn acct-btn--quiet"
              onClick={handleRestore}
              disabled={busy}
            >
              <FiRefreshCw size={14} aria-hidden="true" className={busy ? 'acct-spin' : undefined} />
              {busy ? 'Restoring…' : 'Restore purchases'}
            </button>
          )}
        </div>
      )}

      {message && (
        <p className={`acct-note acct-note--${message.tone}`} role="status">{message.text}</p>
      )}
    </div>
  );
};

export default AccountPanel;
